// Reset MFA modal — used by the agency Users page (operator: agency_admin).
//
// Lockout-recovery primitive for tenant users: clears the target's TOTP so
// they're forced through /setup-totp on next login. Requires step-up TOTP
// (operator re-proves possession of their own authenticator) — defends
// against a hijacked session silently resetting users.
//
// Backend: POST /v1/users/:id/mfa-reset (apps/api/src/routes/v1/users.ts).

import { useState } from "react";
import type { JSX, FormEvent } from "react";
import { apiFetch, ApiError } from "../lib/api.js";

export interface ResetMfaTarget {
  _id:   string;
  email: string;
  role:  string;
}

interface ResetMfaResponse {
  message:        string;
  was_idempotent: boolean;
}

function mapBadRequest(msg: string, fallback: string): string {
  if (msg.includes("operator totp")) return "Invalid code — try again.";
  if (msg.includes("own mfa"))       return "You can't reset your own MFA from this modal.";
  return fallback;
}

function mapResetError(err: unknown): string {
  if (!(err instanceof ApiError)) return "Network error. Try again.";
  const body     = err.body as { error?: string } | null;
  const msg      = (body?.error ?? "").toLowerCase();
  const fallback = body?.error ?? "Could not reset MFA.";

  if (err.status === 404) return "User no longer exists.";
  if (err.status === 403 && msg.includes("super_admin")) return "This user can't be reset from this page.";
  if (err.status === 400) return mapBadRequest(msg, fallback);
  return fallback;
}

// eslint-disable-next-line max-lines-per-function -- modal: form + success state + error mapping
export function ResetMfaModal({ target, onClose, onSuccess }: {
  target:    ResetMfaTarget;
  onClose:   () => void;
  onSuccess: () => void;
}): JSX.Element {
  const [totp, setTotp]             = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [result, setResult]         = useState<ResetMfaResponse | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (totp.length !== 6) { setError("Enter the current 6-digit code from your authenticator app."); return; }
    setError(null);
    setSubmitting(true);
    try {
      const data = await apiFetch<ResetMfaResponse>(`/users/${target._id}/mfa-reset`, {
        method: "POST",
        body:   JSON.stringify({ operator_totp: totp }),
      });
      setResult(data);
    } catch (err) {
      setError(mapResetError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function finish(): void {
    onSuccess();
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        {result ? (
          <>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">MFA reset complete</h2>
            <p className="text-sm text-slate-600 mb-4">{result.message}</p>
            <button
              onClick={finish}
              data-testid="users-reset-mfa-done-btn"
              className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800">
              Done
            </button>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Reset two-factor authentication</h2>
            <p className="text-sm text-slate-500 mb-4">
              For <strong>{target.email}</strong> ({target.role}). They will lose access to their current authenticator
              app and recovery codes, and must enroll a new authenticator on next login.
            </p>

            {error && (
              <div role="alert" className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4" noValidate>
              <div>
                <label htmlFor="op-totp" className="block text-sm font-medium text-slate-700 mb-1">
                  Your authenticator code
                </label>
                <input
                  id="op-totp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  autoComplete="off"
                  data-testid="users-reset-mfa-totp-input"
                  className="w-full text-center text-xl font-mono tracking-widest rounded-lg border border-slate-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-navy-500"
                />
                <p className="mt-1 text-xs text-slate-500">Confirm with your own current 6-digit code.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="flex-1 h-10 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || totp.length !== 6}
                  data-testid="users-reset-mfa-submit-btn"
                  className="flex-1 h-10 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {submitting ? "Resetting…" : "Reset MFA"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
